import { styled } from "@mui/material/styles";
import { paletteVar } from "@platform/design-system";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FC,
} from "react";

import { FONT_SIZE } from "./constants";

const FootnoteTextarea = styled("textarea")({
  width: "100%",
  border: "none",
  outline: "none",
  resize: "none",
  overflow: "hidden",
  boxSizing: "border-box",
  display: "block",
  backgroundColor: "transparent",
  // A native textarea renders its text in the browser's default control colour,
  // which ignores the app theme and stays black on the dark scheme. Pin it to
  // the grid's data-text token so it follows the scheme like the cells around it.
  color: paletteVar("text-primary", "#00344F"),
  fontFamily: "inherit",
  fontSize: FONT_SIZE,
  lineHeight: 1.4,
  paddingLeft: 8,
  textAlign: "left",

  // Keep the placeholder on one line so a narrow column can't wrap it. A plain
  // textarea's scrollHeight already ignores the placeholder, so this is purely
  // visual — the empty-state height stays a single row regardless.
  "&::placeholder": {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
});

export interface FootnoteInputProps {
  serverValue: string;
  onCommit: (value: string) => void;
  placeholder?: string;
  /** Accessible name; distinguishes the row header from the footnote bodies. */
  ariaLabel?: string;
  /**
   * Treat Enter as "commit" instead of inserting a newline. For single-line
   * values like the row header, where a newline is never wanted and Enter
   * reads as "done".
   */
  commitOnEnter?: boolean;
}

/**
 * Free-text footnote editor for the RatesGrid footnote row. Keeps a local draft
 * while editing and commits on blur, matching the inline-input UX of the rate
 * cells rather than AG Grid's separate cell editor.
 *
 * Auto-grows by measuring its own scrollHeight in the real (colSpan-widened)
 * cell — deliberately a plain textarea rather than MUI's TextareaAutosize. Two
 * reasons: TextareaAutosize mis-measures inside AG Grid's off-DOM autoHeight
 * pass (that pass ignores colSpan, so text wraps at one column's width and the
 * row inflates), and it sizes the box to fit the placeholder (wrapping the
 * placeholder in a narrow column also inflates the height). A plain textarea's
 * scrollHeight reflects the value only, so the placeholder never affects height.
 * AG Grid's autoHeight ResizeObserver then sizes the row to this textarea.
 */
export const FootnoteInput: FC<FootnoteInputProps> = ({
  serverValue,
  onCommit,
  placeholder = "Add a footnote",
  ariaLabel = "Footnote",
  commitOnEnter = false,
}) => {
  const [localValue, setLocalValue] = useState(serverValue);
  const isFocusedRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const resize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => {
    if (!isFocusedRef.current) {
      setLocalValue(serverValue);
    }
  }, [serverValue]);

  // Fit to content after every value change, in the real DOM at real width.
  useLayoutEffect(() => {
    resize();
  }, [localValue, resize]);

  // Re-fit when the cell's width changes (column/grid resize) so wrapping — and
  // therefore height — stays correct. Observe the parent, not the textarea, so
  // setting the textarea's height doesn't retrigger the observer.
  useEffect(() => {
    const parent = textareaRef.current?.parentElement;
    if (!parent || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => resize());
    observer.observe(parent);
    return () => observer.disconnect();
  }, [resize]);

  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
  }, []);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setLocalValue(event.target.value);
    },
    [],
  );

  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;
    if (localValue !== serverValue) {
      onCommit(localValue);
    }
  }, [localValue, serverValue, onCommit]);

  // Blurring runs the commit path above, so Enter needs no separate save.
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!commitOnEnter || event.key !== "Enter") return;
      event.preventDefault();
      event.currentTarget.blur();
    },
    [commitOnEnter],
  );

  return (
    <FootnoteTextarea
      ref={textareaRef}
      value={localValue}
      rows={1}
      placeholder={placeholder}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      autoComplete="off"
      maxLength={4096}
      aria-label={ariaLabel}
    />
  );
};
